import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';

import { __t, applyTranslations } from 'utils/language';
import { isRequired } from 'utils/validators';
import { getSelectedSubcampaigns } from 'utils/bulk-offer';

import { PromptExit, navigate } from 'libs/router';

import ActionButton from 'components/buttons/action-button/ActionButton';
import ActionLink from 'components/buttons/action-link/ActionLink';
import DatePickerMulti from 'components/date-picker/date-picker-multi/DatePickerMultiLazy';
import Input from 'components/inputs/input/Input';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import { Form, FormField } from 'components/form';
import Tooltip from 'components/Tooltip';
import SubcampaignsMultiSelect from 'modules/subcampaigns/components/bulk/subcampaigns-multi-select/SubcampaignsMultiSelect';
import { ModalFrame, ModalFrameFooter, ModalFrameInner, ModalFrameMessage } from 'components/modal';

import {
  duplicateCampaign,
  fetchSubcampaigns,
  resetCampaignDuplication
} from '../../reducers/campaignDuplication';
import { danger } from 'redux-store/reducers/common-reducer';

import type {
  Campaign,
  CampaignDuplicationForm,
  Subcampaign,
  SubcampaignSelectedIdsMap,
  TextEntry
} from 'types';


import type { SyntheticEvent } from 'react';


applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./duplicate-campaign-modal.i18n.json'));

type Option = {
  name: string,
  label: string,
  tooltip?: string,
  checked: boolean,
  disabled?: boolean
};

type OwnProps = {
  campaign: Campaign,
  onCloseModal: () => void
};

type StateProps = {
  isLoading: boolean,
  subcampaigns: Array<Subcampaign>
};

type DispatchProps = {
  dispatchFetchSubcampaigns: (a: number) => Promise<any>,
  dispatchDuplicateCampaign: (b: Campaign, a: CampaignDuplicationForm) => Promise<any>,
  dispatchResetCampaignDuplication: () => Promise<any>,
  dispatchDanger: (a: string) => Promise<any>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  isModified: boolean,
  selectedIdsMap: SubcampaignSelectedIdsMap,
  data: CampaignDuplicationForm
};

export class DuplicateCampaignModal extends React.PureComponent<Props, State> {
  state = {
    selectedIdsMap: {},
    isModified: false,
    data: {
      name: '',
      endDate: null,
      startDate: null,
      copyAdditionalRequirementsTemplateRequired: false,
      copyMainImageRequired: false,
      copyAssetsRequired: false
    }
  };

  componentDidMount() {
    const { campaign, dispatchFetchSubcampaigns, dispatchResetCampaignDuplication } = this.props;

    dispatchResetCampaignDuplication();
    dispatchFetchSubcampaigns(campaign.id);
  }

  handleToggle = (subcampaignId: number): void => {
    this.setState(state => ({
      ...state,
      selectedIdsMap: {
        ...state.selectedIdsMap,
        [subcampaignId]: !state.selectedIdsMap[subcampaignId]
      },
      isModified: true
    }));
  };

  handleToggleAll = (subcampaignIds: Array<number>, isSelected: boolean): void => {
    const selectedIdsMap = {};

    subcampaignIds.forEach(id => {
      selectedIdsMap[id] = isSelected;
    });

    this.setState(state => ({
      ...state,
      selectedIdsMap,
      isModified: true
    }));
  };

  handleFieldChanged = (name: string, value: any): void => {
    this.setState(state => ({
      ...state,
      data: {
        ...state.data,
        [name]: value
      },
      isModified: true
    }));
  };

  handleTextChanged = (event: SyntheticEvent<TextEntry>): void => {
    const name = event.currentTarget.name;
    const value = event.currentTarget.value;

    this.handleFieldChanged(name, value);
  };

  handleCheckboxChanged = (event: SyntheticEvent<HTMLInputElement>): void => {
    const name = event.currentTarget.name;
    const checked = event.currentTarget.checked;

    this.handleFieldChanged(name, checked);
  };

  handleDateChanged = (startDate?: string | null, endDate?: string | null): void => {
    this.handleFieldChanged('startDate', startDate);
    this.handleFieldChanged('endDate', endDate);
  };

  handleSubmit = () => {
    const { campaign, subcampaigns, onCloseModal, dispatchDuplicateCampaign, dispatchDanger } = this.props;
    const { data, selectedIdsMap } = this.state;

    const selectedSubcampaigns = getSelectedSubcampaigns(subcampaigns, selectedIdsMap);

    const payload = {
      ...data,
      creatorIds: selectedSubcampaigns.map(s => s.creatorProfile.id)
    };

    dispatchDuplicateCampaign(campaign, payload)
      .then((newCampaign) => {
        if (newCampaign && newCampaign.id) {
          onCloseModal();
          navigate.to(`/campaigns/${newCampaign.id}`);
        } else {
          dispatchDanger(__t('collabary_common.errors.unknown_error'));
        }
      })
      .catch(() => {
        dispatchDanger(__t('collabary_common.errors.unknown_error'));
      });
  };

  renderFormFields(): React.ReactNode | undefined | null {
    const { isLoading } = this.props;
    const { data } = this.state;

    if (isLoading) {
      return null;
    }

    return (
      <React.Fragment>
        <div className="duplicate-campaign-modal__form-field">
          <FormField
            direction="column"
            label={__t('duplicate_campaign_modal.form.field_name')}
            labelType="highlighted"
            showValidationIcon={false}
            showRequiredMark
            validators={[
              isRequired(data.name)
            ]}
          >
            <Input
              name="name"
              value={data.name}
              onChange={this.handleTextChanged}
            />
          </FormField>
        </div>
        <div className="duplicate-campaign-modal__form-field">
          <FormField
            showRequiredMark
            showValidationIcon={false}
            label={__t('duplicate_campaign_modal.form.field_dates')}
            labelType="highlighted"
            direction="column"
            validators={[
              isRequired(data.startDate),
              isRequired(data.endDate)
            ]}
          >
            <DatePickerMulti
              numMonths={1}
              startDateName="startDate"
              startDate={data.startDate}
              endDateName="endDate"
              endDate={data.endDate}
              onChange={this.handleDateChanged}
            />
          </FormField>
        </div>
        {this.renderOption({
          name: 'copyCampaignBackground',
          label: __t('duplicate_campaign_modal.form.field_background'),
          tooltip: __t('duplicate_campaign_modal.form.field_background_tooltip'),
          checked: true,
          disabled: true
        })}
        {this.renderOption({
          name: 'copyMainImageRequired',
          label: __t('duplicate_campaign_modal.form.field_main_image'),
          checked: data.copyMainImageRequired
        })}
        {this.renderOption({
          name: 'copyAssetsRequired',
          label: __t('duplicate_campaign_modal.form.field_assets'),
          checked: data.copyAssetsRequired
        })}
        {this.renderOption({
          name: 'copyAdditionalRequirementsTemplateRequired',
          label: __t('duplicate_campaign_modal.form.field_additional_requirements'),
          checked: data.copyAdditionalRequirementsTemplateRequired
        })}
      </React.Fragment>
    );
  }

  renderOption(
    {
      name,
      label,
      tooltip,
      checked,
      disabled
    }: Option
  ): React.ReactNode {
    const { campaign } = this.props;

    const optionClasses = classnames({
      'duplicate-campaign-modal__option': true,
      'duplicate-campaign-modal__option--disabled': disabled
    });

    const checkboxClasses = classnames({
      'duplicate-campaign-modal__option-checkbox': true,
      'duplicate-campaign-modal__option-checkbox--disabled': disabled
    });

    return (
      <div className={optionClasses}>
        <div className="duplicate-campaign-modal__option-column">
          <div className={checkboxClasses}>
            <InputCheckbox
              id={`duplicate-campaign-option-${name}-${Math.random()}`}
              name={name}
              checked={checked}
              onChange={this.handleCheckboxChanged}
            />
          </div>
          <div className="duplicate-campaign-modal__option-label">
            {label}
          </div>
          {
            tooltip &&
              <div className="duplicate-campaign-modal__question-mark">
                <Tooltip
                  showQuestionMark
                  message={tooltip}
                />
              </div>
          }
        </div>
        <div className="duplicate-campaign-modal__option-column">
          <ActionLink
            openInNewTab
            color="light"
            link={`/campaigns/${campaign.id}`}
          >
            {__t('duplicate_campaign_modal.modal.button_view')}
          </ActionLink>
        </div>
      </div>
    );
  }

  render() {
    const { isLoading, subcampaigns, campaign, onCloseModal } = this.props;
    const { selectedIdsMap, isModified } = this.state;

    return (
      <ModalFrame
        title={__t('duplicate_campaign_modal.modal.title')}
        subtitle={`"${campaign.name}"`}
      >
        {
          isModified &&
            <PromptExit type="modal" />
        }
        <ModalFrameMessage
          message={__t('duplicate_campaign_modal.modal.campaign_desc')}
        />
        <ModalFrameInner>
          <Form
            onSubmit={this.handleSubmit}
          >
            <SubcampaignsMultiSelect
              isLoading={isLoading}
              subcampaigns={subcampaigns}
              selectedIdsMap={selectedIdsMap}
              titleAll={__t('duplicate_campaign_modal.modal.column_title_to_select_from')}
              titleAllCollapsible
              titleAllWithCheckbox
              titleSelected={__t('duplicate_campaign_modal.modal.column_title_selected')}
              messageAllEmpty={__t('duplicate_campaign_modal.modal.message_empty_all')}
              messageSelectedEmpty={__t('duplicate_campaign_modal.modal.message_empty_selected')}
              onToggle={this.handleToggle}
              onToggleAll={this.handleToggleAll}
            >
              {this.renderFormFields()}
            </SubcampaignsMultiSelect>
            {
              !isLoading &&
                <ModalFrameFooter>
                  <ActionButton
                    color="tertiary"
                    onClick={onCloseModal}
                  >
                    {__t('collabary_common.action_button.cancel')}
                  </ActionButton>
                  <ActionButton
                    type="submit"
                    color="secondary"
                  >
                    {__t('duplicate_campaign_modal.modal.button_submit')}
                  </ActionButton>
                </ModalFrameFooter>
            }
          </Form>
        </ModalFrameInner>
      </ModalFrame>
    );
  }
}

const mapStateToProps = ({ campaignDuplication }): StateProps => ({
  isLoading: campaignDuplication.isLoading,
  subcampaigns: campaignDuplication.subcampaigns
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchSubcampaigns: (campaignId: number) => dispatch(fetchSubcampaigns(campaignId)),
  dispatchDuplicateCampaign: (campaign: Campaign, data) => dispatch(duplicateCampaign(campaign, data)),
  dispatchResetCampaignDuplication: () => dispatch(resetCampaignDuplication()),
  dispatchDanger: (message) => dispatch(danger(message))
});

export default connect(mapStateToProps, mapDispatchToProps)(DuplicateCampaignModal);
